/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { PaddingProps, paddingInterpolations } from '../utils/UNSAFE_interpolations/padding';
import { ColorProps, colorInterpolations } from '../utils/UNSAFE_interpolations/colors';
import { AriaProps, ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import {
  baseStyle,
  hoverStyle,
  selectedStyle,
  selectedHoverStyle
} from './themes/SelectionCardStyles.css';
import { useHover } from '../hooks/UNSAFE_useHover';

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;

type Props = StyleProps &
  TestIdProps & {
    children?: ComponentChildren;

    /**
     * This flag marks this card as selected within a collection.
     */
    isSelected?: boolean;
  };

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(ariaInterpolations),
  ...Object.values(paddingInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * A selection card is a container element that is a part of a collection of elements, and renders a group of related information in a styled rectangular area.
 * Width, height, and backgroundColor are set through props.
 */
export const SelectionCard = ({ children, isSelected = false, testId, ...rest }: Props) => {
  const { hoverProps, isHover } = useHover();

  const { class: mergedClasses, ...mergedStyles } = mergedInterpolations(rest);
  const selectionStyles = classNames([
    baseStyle,
    mergedClasses,
    isHover && hoverStyle,
    isSelected && (isHover ? selectedHoverStyle : selectedStyle)
  ]);
  const testIdProps = useTestId(testId);
  return (
    <div
      role="region"
      {...hoverProps}
      class={selectionStyles}
      style={mergedStyles}
      {...testIdProps}>
      {children}
    </div>
  );
};
